import React, { useState } from 'react';
import { Save, X, Trash2, CheckCircle2, XCircle, Package } from 'lucide-react';
import { TimeEntry, Assignment, TimeBank } from '../types';
import { formatHoursMinutes, parseHoursMinutes } from '../utils/timeUtils';
import { supabase } from '../lib/supabase';

interface SparePartHistory {
  part_number: string;
  description: string;
  use_count: number;
  last_used: string;
}

interface Article {
  id: string;
  article_number: string;
  name: string;
  description: string | null;
  recommended_price: number | null;
  stock_quantity: number;
}

interface EditEntryModalProps {
  entry: TimeEntry;
  assignment?: Assignment;
  timeBank?: TimeBank;
  timeBanks?: TimeBank[];
  assignments?: Assignment[];
  editForm: Partial<TimeEntry & {
    assignmentStatus: string;
    spareParts: Array<{quantity?: string; partNumber?: string; description?: string; price?: string}>
  }>;
  sparePartsHistory: SparePartHistory[];
  availableArticles?: Article[];
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onFormChange: (updates: Partial<TimeEntry & {
    assignmentStatus: string;
    spareParts: Array<{quantity?: string; partNumber?: string; description?: string; price?: string}>
  }>) => void;
  onUpdateAssignment?: (id: string, updates: Partial<Assignment>) => void;
  onVerificationToggle?: () => void;
  isVerified?: boolean;
}

export const EditEntryModal: React.FC<EditEntryModalProps> = ({
  entry,
  assignment,
  timeBank,
  timeBanks,
  assignments,
  editForm,
  sparePartsHistory,
  availableArticles = [],
  onSave,
  onCancel,
  onDelete,
  onFormChange,
  onUpdateAssignment,
  onVerificationToggle,
  isVerified
}) => {
  const [showArticleBrowserModal, setShowArticleBrowserModal] = useState(false);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [selectedArticleIndex, setSelectedArticleIndex] = useState<number | null>(null);
  const [articlesSaveToRegistry, setArticlesSaveToRegistry] = useState<{[key: number]: boolean}>({});

  const handleSaveWithRegistry = async () => {
    // Check if entry is locked
    if ((entry as any).isLocked) {
      alert('Denna tidspost är låst och kan inte redigeras. Den kan vara kopplad till en faktura eller en avslutad tidsbank.');
      return;
    }

    // Validate non-billable reason if not billable
    if (editForm.isBillable === false && (!editForm.nonBillableReason || !editForm.nonBillableReason.trim())) {
      alert('Du måste ange en anledning när tidsposten inte är fakturerbar');
      return;
    }

    // Save articles to registry if requested
    if (editForm.spareParts && Array.isArray(editForm.spareParts)) {
      for (let i = 0; i < editForm.spareParts.length; i++) {
        const article = editForm.spareParts[i];
        if (articlesSaveToRegistry[i] && article.partNumber) {
          const articleExists = availableArticles.some(a => a.article_number === article.partNumber);
          if (!articleExists) {
            const { error: articleError } = await supabase
              .from('articles')
              .insert({
                article_number: article.partNumber,
                name: article.description || article.partNumber,
                description: article.description || '',
              });

            if (articleError && articleError.code !== '23505') {
              console.error('Error saving to registry:', articleError);
            }
          }
        }
      }
    }

    setArticlesSaveToRegistry({});
    onSave();
  };

  const calculateTimeDifference = (startTime: string, endTime: string): number | null => {
    if (!startTime || !endTime) return null;

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    let diff = endTotalMinutes - startTotalMinutes;
    if (diff < 0) diff += 24 * 60;

    return diff;
  };

  const setEditForm = (updates: any) => {
    const newForm = { ...editForm, ...updates };

    if ('workStartTime' in updates || 'workEndTime' in updates) {
      const calculatedTime = calculateTimeDifference(
        newForm.workStartTime || '',
        newForm.workEndTime || ''
      );
      if (calculatedTime !== null) {
        newForm.workTime = calculatedTime;
      }
    }

    if ('travelStartTime' in updates || 'travelEndTime' in updates) {
      const calculatedTime = calculateTimeDifference(
        newForm.travelStartTime || '',
        newForm.travelEndTime || ''
      );
      if (calculatedTime !== null) {
        newForm.travelTime = calculatedTime;
      }
    }

    if ('returnTravelStartTime' in updates || 'returnTravelEndTime' in updates) {
      const calculatedTime = calculateTimeDifference(
        newForm.returnTravelStartTime || '',
        newForm.returnTravelEndTime || ''
      );
      if (calculatedTime !== null) {
        newForm.returnTravelTime = calculatedTime;
      }
    }

    onFormChange(newForm);
  };

  const activeTimeBanks = timeBanks?.filter(tb => tb.status === 'active') || [];
  const availableAssignments = assignments?.filter(a =>
    a.timeBankId === (editForm.timeBankId || entry.timeBankId) && a.status === 'active'
  ) || [];

  const isLocked = (entry as any).isLocked;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Redigera tidsregistrering</h2>
            <p className="text-sm text-gray-600 mt-1">
              {timeBank?.customerName} - {assignment?.name}
            </p>
            {isLocked && (
              <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">
                  Denna tidspost är låst och kan inte redigeras
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Tidsposten kan vara kopplad till en faktura eller en avslutad tidsbank
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center w-full">
            <div>
              {onDelete && !isLocked && (
                <button
                  onClick={onDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Ta bort
                </button>
              )}
            </div>
            <div className="flex space-x-2">
              {onVerificationToggle && !isLocked && (
                <button
                  onClick={onVerificationToggle}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isVerified
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {isVerified ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      OK
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      Ej godkänd
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleSaveWithRegistry}
                disabled={isLocked}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isLocked
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Save className="h-4 w-4" />
                Spara
              </button>
              <button
                onClick={onCancel}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
                Avbryt
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Fakturerbar tidspost - Always visible at top */}
          <div className="mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-400">
              <div className="flex items-start space-x-3 mb-3">
                <input
                  type="checkbox"
                  id={`isBillable-modal-${entry.id}`}
                  checked={editForm.isBillable !== undefined ? editForm.isBillable : (entry.isBillable !== false)}
                  onChange={(e) => {
                    setEditForm({
                      isBillable: e.target.checked,
                      nonBillableReason: e.target.checked ? undefined : editForm.nonBillableReason
                    });
                  }}
                  className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor={`isBillable-modal-${entry.id}`} className="block text-base font-semibold text-blue-900">
                    Fakturerbar tidspost
                  </label>
                  <p className="text-sm text-blue-700 mt-1">
                    Avmarkera om denna tidspost inte ska faktureras kunden
                  </p>
                </div>
              </div>

              {(editForm.isBillable === false || (editForm.isBillable === undefined && entry.isBillable === false)) && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Anledning till icke-fakturering <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editForm.nonBillableReason !== undefined ? editForm.nonBillableReason : (entry.nonBillableReason || '')}
                    onChange={(e) => setEditForm({ nonBillableReason: e.target.value })}
                    className="w-full px-3 py-2 border border-blue-300 rounded text-sm"
                    rows={2}
                    placeholder="Förklara varför denna tidspost inte ska faktureras..."
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            {/* Timbank */}
            {timeBanks && timeBanks.length > 0 && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-yellow-900 mb-3">Timbank</h5>
                <select
                  value={editForm.timeBankId || entry.timeBankId}
                  onChange={(e) => {
                    setEditForm({ timeBankId: e.target.value });
                    // Reset assignment when changing timebank
                    const newAssignments = assignments?.filter(a => a.timeBankId === e.target.value && a.status === 'active') || [];
                    if (newAssignments.length > 0) {
                      setEditForm({ timeBankId: e.target.value, assignmentId: newAssignments[0].id });
                    } else {
                      setEditForm({ timeBankId: e.target.value, assignmentId: '' });
                    }
                  }}
                  className="w-full px-3 py-2 border border-yellow-300 rounded text-sm"
                >
                  {activeTimeBanks.map(tb => (
                    <option key={tb.id} value={tb.id}>
                      {tb.customerName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Uppdrag */}
            {assignments && assignments.length > 0 && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-yellow-900 mb-3">Uppdrag</h5>
                <select
                  value={editForm.assignmentId || entry.assignmentId}
                  onChange={(e) => setEditForm({ assignmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-yellow-300 rounded text-sm"
                >
                  {availableAssignments.map(a => {
                    const assignmentCustomer = timeBanks?.find(tb => tb.id === a.timeBankId);
                    return (
                      <option key={a.id} value={a.id}>
                        {assignmentCustomer?.customerName ? `${assignmentCustomer.customerName} - ${a.name}` : a.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Arbetstid */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-blue-900 mb-3">Arbetstid</h5>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Start</label>
                    <input
                      type="time"
                      value={editForm.workStartTime || ''}
                      onChange={(e) => setEditForm({ workStartTime: e.target.value })}
                      className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Slut</label>
                    <input
                      type="time"
                      value={editForm.workEndTime || ''}
                      onChange={(e) => setEditForm({ workEndTime: e.target.value })}
                      className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-blue-700 mb-1">Manuell tid</label>
                  <input
                    type="text"
                    value={editForm.workTime ? formatHoursMinutes(editForm.workTime) : ''}
                    onChange={(e) => setEditForm({ workTime: parseHoursMinutes(e.target.value) })}
                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
                    placeholder="1h 30min"
                  />
                </div>
              </div>
            </div>

            {/* Restid */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-green-900 mb-3">Restid</h5>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-green-700 mb-1">Start</label>
                    <input
                      type="time"
                      value={editForm.travelStartTime || ''}
                      onChange={(e) => setEditForm({ travelStartTime: e.target.value })}
                      className="w-full px-2 py-1 border border-green-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-green-700 mb-1">Slut</label>
                    <input
                      type="time"
                      value={editForm.travelEndTime || ''}
                      onChange={(e) => setEditForm({ travelEndTime: e.target.value })}
                      className="w-full px-2 py-1 border border-green-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-green-700 mb-1">Manuell tid</label>
                  <input
                    type="text"
                    value={editForm.travelTime ? formatHoursMinutes(editForm.travelTime) : ''}
                    onChange={(e) => setEditForm({ travelTime: parseHoursMinutes(e.target.value) })}
                    className="w-full px-2 py-1 border border-green-300 rounded text-sm"
                    placeholder="30min"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`virtualTravel-modal-${entry.id}`}
                    checked={editForm.isVirtualTravel || false}
                    onChange={(e) => setEditForm({ isVirtualTravel: e.target.checked })}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`virtualTravel-modal-${entry.id}`} className="ml-2 block text-xs text-green-700">
                    Virtuell resa (ingen fysisk förflyttning)
                  </label>
                </div>
              </div>
            </div>

            {/* Returresa */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-purple-900 mb-3">Returresa</h5>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-purple-700 mb-1">Start</label>
                    <input
                      type="time"
                      value={editForm.returnTravelStartTime || ''}
                      onChange={(e) => setEditForm({ returnTravelStartTime: e.target.value })}
                      className="w-full px-2 py-1 border border-purple-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-purple-700 mb-1">Slut</label>
                    <input
                      type="time"
                      value={editForm.returnTravelEndTime || ''}
                      onChange={(e) => setEditForm({ returnTravelEndTime: e.target.value })}
                      className="w-full px-2 py-1 border border-purple-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-purple-700 mb-1">Manuell tid</label>
                  <input
                    type="text"
                    value={editForm.returnTravelTime ? formatHoursMinutes(editForm.returnTravelTime) : ''}
                    onChange={(e) => setEditForm({ returnTravelTime: parseHoursMinutes(e.target.value) })}
                    className="w-full px-2 py-1 border border-purple-300 rounded text-sm"
                    placeholder="30min"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`virtualReturnTravel-modal-${entry.id}`}
                    checked={editForm.isVirtualReturnTravel || false}
                    onChange={(e) => setEditForm({ isVirtualReturnTravel: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`virtualReturnTravel-modal-${entry.id}`} className="ml-2 block text-xs text-purple-700">
                    Virtuell returresa (ingen fysisk förflyttning)
                  </label>
                </div>
              </div>
            </div>

            {/* Kilometer och kostnader */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-orange-900 mb-3">Kilometer och kostnader</h5>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-orange-700 mb-1">Km dit</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={editForm.outbound_km ?? 0}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      setEditForm({ outbound_km: isNaN(val) ? 0 : val });
                    }}
                    className="w-full px-2 py-1 border border-orange-300 rounded text-sm"
                  />
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id={`virtualOutbound-modal-${entry.id}`}
                      checked={editForm.isVirtualTravel || false}
                      onChange={(e) => setEditForm({ isVirtualTravel: e.target.checked })}
                      className="h-3 w-3 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`virtualOutbound-modal-${entry.id}`} className="ml-1 block text-xs text-orange-700">
                      Virtuell
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-orange-700 mb-1">Km hem</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={editForm.return_km ?? 0}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      setEditForm({ return_km: isNaN(val) ? 0 : val });
                    }}
                    className="w-full px-2 py-1 border border-orange-300 rounded text-sm"
                  />
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id={`virtualReturn-modal-${entry.id}`}
                      checked={editForm.isVirtualReturnTravel || false}
                      onChange={(e) => setEditForm({ isVirtualReturnTravel: e.target.checked })}
                      className="h-3 w-3 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`virtualReturn-modal-${entry.id}`} className="ml-1 block text-xs text-orange-700">
                      Virtuell
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-orange-700 mb-1">Parkering (kr)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.parkingCost ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? null : parseFloat(e.target.value);
                      setEditForm({ parkingCost: (val === null || isNaN(val)) ? null : val });
                    }}
                    className="w-full px-2 py-1 border border-orange-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Datum och plats */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-gray-900 mb-3">Datum och plats</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-700 mb-1">Datum</label>
                  <input
                    type="date"
                    value={editForm.date || ''}
                    onChange={(e) => setEditForm({ date: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-1">Plats</label>
                  <input
                    type="text"
                    value={editForm.location || ''}
                    onChange={(e) => setEditForm({ location: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Beskrivning */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-gray-900 mb-3">Beskrivning</h5>
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                rows={3}
                placeholder="Beskriv arbetet..."
              />
            </div>

            {/* Uppdragsstatus */}
            {onUpdateAssignment && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-yellow-900 mb-3">Uppdragsstatus</h5>
                <select
                  value={editForm.assignmentStatus || 'active'}
                  onChange={(e) => setEditForm({ assignmentStatus: e.target.value })}
                  className="w-full px-3 py-2 border border-yellow-300 rounded text-sm"
                >
                  <option value="active">Aktiv</option>
                  <option value="completed">Slutförd</option>
                </select>
                <p className="text-xs text-yellow-700 mt-2">
                  Ändra status på uppdraget "{assignment?.name}"
                </p>
              </div>
            )}

            {/* Artiklar / Reservdelar */}
            {onUpdateAssignment && (
              <div className="bg-blue-50 p-4 rounded-lg lg:col-span-3">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-medium text-blue-900">Artiklar / Reservdelar</h5>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    {availableArticles.length} artiklar i register
                  </span>
                </div>
                <div className="space-y-2">
                  {(editForm.spareParts || []).map((article, idx) => {
                    const isFromRegistry = availableArticles.some(a => a.article_number === article.partNumber);
                    return (
                      <div key={idx} className={`flex gap-2 items-start bg-white p-3 rounded border-2 transition-colors ${isFromRegistry ? 'border-green-300 bg-green-50' : 'border-blue-200'}`}>
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-5 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Antal</label>
                              <input
                                type="text"
                                placeholder="1"
                                value={article.quantity || ''}
                                onChange={(e) => {
                                  const updated = [...(editForm.spareParts || [])];
                                  updated[idx] = { ...updated[idx], quantity: e.target.value };
                                  setEditForm({ spareParts: updated });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div className="relative col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Välj från register</label>
                              <div className="flex gap-2">
                                <select
                                  value={article.partNumber || ''}
                                  onChange={(e) => {
                                    const updated = [...(editForm.spareParts || [])];
                                    const selectedArticleNumber = e.target.value;
                                    updated[idx] = { ...updated[idx], partNumber: selectedArticleNumber };

                                    if (selectedArticleNumber) {
                                      const registryMatch = availableArticles.find(a => a.article_number === selectedArticleNumber);
                                      if (registryMatch) {
                                        updated[idx].description = registryMatch.name || registryMatch.description || '';
                                        updated[idx].price = registryMatch.recommended_price?.toString() || '';
                                      } else {
                                        const historyMatch = sparePartsHistory.find(p => p.part_number === selectedArticleNumber);
                                        if (historyMatch && !updated[idx].description) {
                                          updated[idx].description = historyMatch.description;
                                        }
                                      }
                                    }

                                    setEditForm({ spareParts: updated });
                                  }}
                                  className={`flex-1 px-3 py-2 border rounded text-sm focus:ring-1 ${isFromRegistry ? 'border-green-500 focus:border-green-600 focus:ring-green-500 bg-green-50' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
                                >
                                  <option value="">-- Välj artikel --</option>
                                  {availableArticles.length > 0 && (
                                    <optgroup label="📦 Artikelregister">
                                      {availableArticles.map((art) => (
                                        <option key={art.id} value={art.article_number}>
                                          {art.article_number} - {art.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {sparePartsHistory.length > 0 && (
                                    <optgroup label="🕐 Tidigare använda">
                                      {sparePartsHistory
                                        .filter(h => !availableArticles.some(a => a.article_number === h.part_number))
                                        .map((part, histIdx) => (
                                          <option key={`hist-${histIdx}`} value={part.part_number}>
                                            {part.part_number} - {part.description}
                                          </option>
                                        ))}
                                    </optgroup>
                                  )}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedArticleIndex(idx);
                                    setShowArticleBrowserModal(true);
                                  }}
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                  title="Bläddra i artikelregister"
                                >
                                  <Package className="h-5 w-5" />
                                </button>
                              </div>
                              {isFromRegistry && (
                                <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                  ✓
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Beskrivning</label>
                              <input
                                type="text"
                                placeholder="Beskrivning"
                                value={article.description || ''}
                                onChange={(e) => {
                                  const updated = [...(editForm.spareParts || [])];
                                  updated[idx] = { ...updated[idx], description: e.target.value };
                                  setEditForm({ spareParts: updated });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                readOnly={isFromRegistry}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Pris (kr)</label>
                              <input
                                type="text"
                                placeholder="0"
                                value={article.price || ''}
                                onChange={(e) => {
                                  const updated = [...(editForm.spareParts || [])];
                                  updated[idx] = { ...updated[idx], price: e.target.value };
                                  setEditForm({ spareParts: updated });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-700">Eller skriv eget artikelnr:</label>
                            <input
                              type="text"
                              placeholder="Skriv artikelnummer..."
                              value={article.partNumber || ''}
                              onChange={(e) => {
                                const updated = [...(editForm.spareParts || [])];
                                updated[idx] = { ...updated[idx], partNumber: e.target.value };

                                const registryMatch = availableArticles.find(a => a.article_number === e.target.value);
                                if (registryMatch) {
                                  updated[idx].description = registryMatch.name || registryMatch.description || '';
                                  updated[idx].price = registryMatch.recommended_price?.toString() || '';
                                  setArticlesSaveToRegistry({ ...articlesSaveToRegistry, [idx]: false });
                                }

                                setEditForm({ spareParts: updated });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          {isFromRegistry ? (
                            <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Artikel från artikelregister
                            </div>
                          ) : article.partNumber && (
                            <div className="flex items-center gap-2 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded">
                              <input
                                type="checkbox"
                                id={`saveToRegistry-${idx}`}
                                checked={articlesSaveToRegistry[idx] || false}
                                onChange={(e) => setArticlesSaveToRegistry({ ...articlesSaveToRegistry, [idx]: e.target.checked })}
                                className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`saveToRegistry-${idx}`} className="text-xs text-yellow-800 cursor-pointer">
                                Vill du lägga till denna artikel i registret för framtida användning?
                              </label>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            const updated = (editForm.spareParts || []).filter((_, i) => i !== idx);
                            setEditForm({ spareParts: updated });
                          }}
                          className="text-red-600 hover:text-red-800 mt-6"
                          title="Ta bort artikel"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => {
                      const updated = [...(editForm.spareParts || []), { quantity: '1', partNumber: '', description: '', price: '' }];
                      setEditForm({ spareParts: updated });
                    }}
                    className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded text-sm text-blue-700 hover:bg-blue-100 hover:border-blue-400 transition-colors font-medium"
                  >
                    + Lägg till artikel
                  </button>
                </div>
                <div className="mt-3 p-3 bg-blue-100 rounded text-xs text-blue-800">
                  <p className="font-medium mb-2">💡 Så här använder du artikelregistret:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Välj från register:</strong> Klicka på dropdown-menyn för att se alla tillgängliga artiklar</li>
                    <li><strong>Eller skriv eget:</strong> Skriv artikelnummer manuellt i fältet under</li>
                    <li><strong>Automatisk ifyllning:</strong> När du väljer en artikel fylls beskrivning och pris i automatiskt</li>
                    <li><strong>Grön markering:</strong> Artiklar från registret får grön kant och checkmark</li>
                    <li><strong>Nya artiklar:</strong> Egna artikelnummer sparas automatiskt till registret</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showArticleBrowserModal && selectedArticleIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
              <div>
                <h2 className="text-xl font-bold">Artikelregister</h2>
                <p className="text-sm text-blue-100">Välj en artikel från listan</p>
              </div>
              <button
                onClick={() => {
                  setShowArticleBrowserModal(false);
                  setArticleSearchTerm('');
                  setSelectedArticleIndex(null);
                }}
                className="text-white hover:bg-blue-700 rounded p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Sök artikelnummer, namn eller beskrivning..."
                  value={articleSearchTerm}
                  onChange={(e) => setArticleSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Package className="h-5 w-5" />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {availableArticles.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Inga artiklar i registret</p>
                  <p className="text-sm text-gray-400">Gå till Register → Artikelregister för att lägga till artiklar</p>
                </div>
              ) : (
                <>
                  {availableArticles.filter(article => {
                    if (!articleSearchTerm.trim()) return true;
                    const term = articleSearchTerm.toLowerCase();
                    return (
                      article.article_number.toLowerCase().includes(term) ||
                      (article.name && article.name.toLowerCase().includes(term)) ||
                      (article.description && article.description.toLowerCase().includes(term))
                    );
                  }).length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-2">Hittade ingen artikel som matchar sökningen</p>
                      <p className="text-sm text-gray-400 mb-4">Prova att söka på annat eller stäng detta fönster och skriv in artikeln manuellt</p>
                      <button
                        onClick={() => setArticleSearchTerm('')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Rensa sökning
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Artikelnummer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Namn
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Beskrivning
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Pris (kr)
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Åtgärd
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {availableArticles
                            .filter(article => {
                              if (!articleSearchTerm.trim()) return true;
                              const term = articleSearchTerm.toLowerCase();
                              return (
                                article.article_number.toLowerCase().includes(term) ||
                                (article.name && article.name.toLowerCase().includes(term)) ||
                                (article.description && article.description.toLowerCase().includes(term))
                              );
                            })
                            .map((article) => (
                              <tr key={article.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {article.article_number}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-900">{article.name || '-'}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500">{article.description || '-'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {article.recommended_price ?
                                      `${article.recommended_price.toFixed(2)} kr` :
                                      '-'
                                    }
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => {
                                      const updated = [...(editForm.spareParts || [])];
                                      updated[selectedArticleIndex] = {
                                        ...updated[selectedArticleIndex],
                                        partNumber: article.article_number,
                                        description: article.name || article.description || '',
                                        price: article.recommended_price?.toString() || ''
                                      };
                                      setEditForm({ spareParts: updated });
                                      setShowArticleBrowserModal(false);
                                      setArticleSearchTerm('');
                                      setSelectedArticleIndex(null);
                                    }}
                                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                  >
                                    Välj
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Hittar du inte artikeln?</strong> Stäng detta fönster och använd fältet "Eller skriv artikelnummer manuellt" för att lägga till artikeln direkt. Du kan även gå till Register → Artikelregister för att lägga till nya artiklar i systemet.
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Visar {availableArticles.filter(article => {
                    if (!articleSearchTerm.trim()) return true;
                    const term = articleSearchTerm.toLowerCase();
                    return (
                      article.article_number.toLowerCase().includes(term) ||
                      (article.name && article.name.toLowerCase().includes(term)) ||
                      (article.description && article.description.toLowerCase().includes(term))
                    );
                  }).length} av {availableArticles.length} artiklar
                </p>
                <button
                  onClick={() => {
                    setShowArticleBrowserModal(false);
                    setArticleSearchTerm('');
                    setSelectedArticleIndex(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Stäng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
